// ============================================================================
// SupportActor — WHO rang this up, when the answer is "nobody who works here".
// ----------------------------------------------------------------------------
// An OrderUp staff console session (POS staff flavor, or admin "Enter as
// support") operates inside a merchant's restaurant with a support-scope token.
// Every document it writes needs an answer to "who did this", and the merchant's
// roster has no row that is a truthful answer — the operator is not their
// employee.
//
// The rejected alternative, recorded here so it is not re-proposed: give support
// a roster row with a new `EmployeeAccountType`. 97 services read `employees`,
// and only THREE of them filter on the account type discriminator. A new value
// would be silently included by the other 94 — payroll runs, tip pools, labor
// reports, scheduling — and an OrderUp engineer would draw a paycheck from a
// restaurant they have never been to. The failure is silent and financial, which
// is the worst pair.
//
// So attribution lives on the WRITE, not on the roster:
//
//   • `createdByEmployeeId` stays ABSENT. It is already optional everywhere, so
//     every existing reader already handles "no employee" — nothing had to be
//     taught a new value. A support write is genuinely employee-less; saying so
//     is more honest than inventing an id, and an invented id is exactly what
//     payroll would later pay.
//   • `supportActor` carries the answer instead, and every field in it except
//     the display label is pinned by `firestore.rules` to the caller's own
//     token. A client cannot write someone else's name onto its work.
//
// ## Why the label is denormalised
//
// `uid` is the durable identity, but a uid is not a name and `admins/{uid}` is
// not retained forever. Financial documents are kept 10 years
// (docs/DATA_RETENTION_AND_DELETION_POLICY.md); an OrderUp employee record is
// not. A check from 2031 that says `supportActor.uid: 'x7Kq...'` and nothing
// else is unauditable the moment that admin is offboarded. The label is frozen
// at write time so the document survives the directory it was derived from.
//
// It is still pinned by rules (see `supportActorLabel` below) rather than left
// free-text, because a label nobody verifies is a label that drifts — and the
// one place a drifted label surfaces is a dispute years later, where it reads as
// evidence.
// ============================================================================

/**
 * The only `kind` a `SupportActor` may carry.
 *
 * Deliberately narrow. Classic masquerade (`kind: 'user'`) writes as the TARGET
 * user and is denied the sensitive writes this block accompanies — a masquerade
 * write is recorded under a real employee's name, which is the reason it is
 * blocked rather than attributed. Only self-support, where the operator acts as
 * themselves, ever stamps an actor.
 */
export const SUPPORT_ACTOR_KIND = 'support-scope' as const;

/**
 * Prefix of the frozen display label. Pinned in `firestore.rules`, so changing
 * this string is a rules change too — the guard
 * `check-support-actor-label-parity` fails the build if the two disagree.
 */
export const SUPPORT_ACTOR_LABEL_PREFIX = 'OrderUp Support: ' as const;

/**
 * Attribution stamped on a document written during an OrderUp support session.
 *
 * Present iff the writing session was self-support. Absent on every merchant
 * till, which is the overwhelming majority of writes — readers must treat
 * absence as "an ordinary employee wrote this", never as "unknown actor".
 */
export interface SupportActor {
  /**
   * The operator's own Firebase uid. Pinned by rules to `request.auth.uid`, so
   * it is the uid that actually made the write and cannot name anyone else.
   */
  readonly uid: string;

  /**
   * The operator's email, as carried on their ID token. Pinned by rules to
   * `request.auth.token.email`.
   */
  readonly email: string;

  /**
   * The `impersonation_sessions` document this write belongs to. Pinned by
   * rules to `request.auth.token.impersonator.sessionId`, which is how a
   * document joins back to the session's reason, escalations and end time.
   */
  readonly sessionId: string;

  /** Always {@link SUPPORT_ACTOR_KIND}. Pinned by rules. */
  readonly kind: typeof SUPPORT_ACTOR_KIND;

  /**
   * Frozen display label — what a human reads on a receipt, a report or an
   * audit export once the OrderUp directory no longer holds this uid. Pinned by
   * rules to {@link supportActorLabel} of the verified email.
   */
  readonly label: string;

  /**
   * When the write happened. Written as a server timestamp and pinned by rules
   * to `request.time`, so it is the server's clock, not the till's — a POS
   * clock can be days out and this field is read as evidence.
   */
  readonly at: Date;

  /**
   * Present iff the operator was ACTING AS someone at this location (OrderUp
   * Console act-as, docs/superpowers/specs/2026-10-20-console-act-as-design.md).
   *
   * Two identities on one record, as "Log in as" works elsewhere: the employee
   * fields name the SUBJECT (whose sales, tips and payroll this is), and this
   * block names the ACTOR — the OrderUp operator who actually did it. Absent on
   * a plain support write, where the employee fields stay empty.
   *
   *   - `employee` — `employeeId` is the subject; the record's own employee
   *     fields carry the same id, deliberately (the reversal of the rule above
   *     that a support write leaves them empty).
   *   - `job` — the operator held one job title's permissions and NO person:
   *     no employee field is set, nobody is credited, and reports group the
   *     record under "OrderUp Support".
   */
  readonly actingAs?: SupportActingAs;
}

/** Who the operator was acting as — see {@link SupportActor.actingAs}. */
export interface SupportActingAs {
  readonly mode: 'employee' | 'job';
  /** The subject employee's numeric id — employee mode only. */
  readonly employeeId?: number;
  /** The job title's id (its numeric `jobId`, as a string) — job mode only. */
  readonly jobId?: string;
}

/** Structural check of an `actingAs` block — the same shape `firestore.rules` admits. */
export function isSupportActingAs(value: unknown): value is SupportActingAs {
  if (typeof value !== 'object' || value === null) return false;
  const v = value as Record<string, unknown>;
  if (v.mode === 'employee') {
    return typeof v.employeeId === 'number' && Number.isInteger(v.employeeId) && v.employeeId > 0 && v.jobId === undefined;
  }
  if (v.mode === 'job') {
    return typeof v.jobId === 'string' && v.jobId.length > 0 && v.employeeId === undefined;
  }
  return false;
}

/**
 * True iff the record was performed by OrderUp Support — with or without an
 * act-as subject. The "Performed by OrderUp Support" report dimension.
 */
export function performedBySupport(record: { supportActor?: unknown } | null | undefined): boolean {
  return isSupportActor(record?.supportActor);
}

/**
 * True iff the record was rung by support acting as a JOB TITLE (no person).
 * Such records credit nobody; reports group them under {@link SUPPORT_REPORT_GROUP_LABEL}.
 */
export function isSupportJobModeRecord(record: { supportActor?: unknown } | null | undefined): boolean {
  const actor = record?.supportActor;
  return isSupportActor(actor) && (actor as SupportActor).actingAs?.mode === 'job';
}

/**
 * True iff the ORDER was OPENED by support acting as a JOB TITLE — its frozen
 * `supportOpener` (set on create only, immutable; review 2026-10-20 I5). Read
 * this, never `supportActor`, to decide "who opened it": `supportActor` is the
 * last support toucher.
 */
export function openedBySupportJobMode(record: { supportOpener?: unknown } | null | undefined): boolean {
  const opener = record?.supportOpener;
  return isSupportActingAs(opener) && opener.mode === 'job';
}

/** The group a report files support records that credit nobody under. */
export const SUPPORT_REPORT_GROUP_LABEL = 'OrderUp Support' as const;

/**
 * The canonical label for an operator's email. The single definition of the
 * string `firestore.rules` pins, so no caller composes it by hand.
 *
 * @example supportActorLabel('kevin@letsorderup.com')
 * // => 'OrderUp Support: kevin@letsorderup.com'
 */
export function supportActorLabel(email: string): string {
  return `${SUPPORT_ACTOR_LABEL_PREFIX}${email}`;
}

/**
 * True iff `value` is a structurally complete {@link SupportActor}.
 *
 * Structural only — it proves the block is well formed, NOT that it is
 * truthful. Truth is established by `firestore.rules` at write time, and a
 * reader that trusts this function in place of the rules is trusting the
 * document to vouch for itself. Use it to decide whether a block can be
 * RENDERED, never to decide whether a write may proceed.
 */
export function isSupportActor(value: unknown): value is SupportActor {
  if (typeof value !== 'object' || value === null) return false;
  const v = value as Record<string, unknown>;
  return (
    typeof v.uid === 'string' &&
    v.uid.length > 0 &&
    typeof v.email === 'string' &&
    v.email.length > 0 &&
    typeof v.sessionId === 'string' &&
    v.sessionId.length > 0 &&
    v.kind === SUPPORT_ACTOR_KIND &&
    typeof v.label === 'string' &&
    v.label.length > 0 &&
    (v.at instanceof Date || typeof (v.at as { toDate?: unknown })?.toDate === 'function')
  );
}

/**
 * What to show where an employee name would normally go.
 *
 * Every surface that renders "who did this" — receipts, the order header, the
 * sales report's ticket owner, the check's server — needs the same answer, and
 * needs it to be obviously not-an-employee rather than a blank. A blank reads as
 * a bug and sends a manager looking for a missing roster row.
 *
 * Returns `null` when there is no support actor, so a caller can fall through to
 * its own employee lookup with a single `??`.
 */
export function describeSupportActor(value: unknown): string | null {
  return isSupportActor(value) ? value.label : null;
}

/**
 * The SHORT form, for surfaces that are physically narrow — a kitchen chit's
 * server line, a floor-map tile, a column in a dense table.
 *
 * 48 columns is a whole 80mm chit line (`docs/` print rendering: len() is not
 * columns), and `OrderUp Support: <the agent's address>` is longer than that
 * on its own. Printed in full it wraps or truncates, and a truncated email is
 * worse than no email — it looks like a corrupted name.
 *
 * DISPLAY ONLY, and never stored. The frozen {@link SupportActor.label} is what
 * goes on the document and what a merchant matches against their support access
 * log; this is a rendering of it. Storing this instead would lose the one field
 * that survives the OrderUp directory, which is the entire reason the label is
 * frozen.
 *
 * What a runner needs from a chit is "this was not rung by one of your
 * servers" — the WHO is on the ticket and in the access log, both of which are
 * reachable and neither of which is a piece of paper in a hot kitchen.
 */
export function supportActorShortLabel(value: unknown): string | null {
  return isSupportActor(value) ? 'OrderUp Support' : null;
}

/**
 * Rehydrate a `supportActor` field read back from Firestore.
 *
 * Firestore returns `at` as a `Timestamp`, a document written by an older POS
 * build may hold an ISO string, and a document written before this field
 * existed holds nothing at all. {@link SupportActor} declares a `Date`, so
 * passing the raw map straight through is a typed lie that only detonates at
 * the surface formatting a receipt — which is the one place a crash is
 * expensive.
 *
 * Returns `undefined` for anything that is not a complete block. A partial
 * actor is worse than none: it renders as a half-name on a document that is
 * read as evidence.
 */
export function parseSupportActor(value: unknown): SupportActor | undefined {
  if (typeof value !== 'object' || value === null) return undefined;
  const v = value as Record<string, unknown>;

  let at: Date | undefined;
  if (v.at instanceof Date) {
    at = v.at;
  } else if (typeof v.at === 'string') {
    at = new Date(v.at);
  } else if (
    v.at &&
    typeof v.at === 'object' &&
    typeof (v.at as { toDate?: unknown }).toDate === 'function'
  ) {
    at = (v.at as { toDate: () => Date }).toDate();
  }
  if (!at || Number.isNaN(at.getTime())) return undefined;

  // A malformed `actingAs` is dropped, never rendered: the actor is still
  // truthful, and a half-subject on evidence is worse than none.
  const { actingAs, ...rest } = v;
  const candidate = isSupportActingAs(actingAs) ? { ...rest, at, actingAs } : { ...rest, at };
  return isSupportActor(candidate) ? (candidate as unknown as SupportActor) : undefined;
}
